import { DateRange, DatePickerPreset } from "@bebit/types";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  previousMonday,
  previousSunday,
  startOfToday,
  isMonday,
  startOfDay,
  endOfDay,
  subDays,
  differenceInCalendarDays,
  differenceInDays,
} from "date-fns";

export const MAX_PERIOD_DAYS = 30;

export function getDateRangeOfPeriodChoice(type: DatePickerPreset, maxDate?: Date) {
  const today = startOfToday();

  switch (type) {
    case DatePickerPreset.LastMonth:
      return getDateRangeOfLastMonth(today, maxDate);
    case DatePickerPreset.LastWeek:
      return getDateRangeOfLastWeek(today, maxDate);
    case DatePickerPreset.ThisMonth:
      return getDateRangeOfThisMonth(today);
    case DatePickerPreset.ThisWeek:
      return getDateRangeOfThisWeek(today);
    case DatePickerPreset.Last28Days:
      return getDateRangeOfXDaysFromDate(subDays(today, 2), 27);
    case DatePickerPreset.Last7Days:
      return getDateRangeOfXDaysFromDate(subDays(today, 2), 6);
    default:
      return getDateRangeOfThisWeek(today);
  }
}

function getDateRangeOfThisWeek(date: Date): DateRange {
  const monday = isMonday(date) ? date : previousMonday(date);
  const result: DateRange = {
    start: monday,
    end: endOfDay(subDays(date, 2)),
  };

  return result;
}

function getDateRangeOfLastWeek(date: Date, maxDate?: Date): DateRange {
  const sunday = previousSunday(date);
  const monday = previousMonday(sunday);

  return {
    start: monday,
    end: maxDate && sunday >= maxDate ? maxDate : sunday,
  };
}

function getDateRangeOfThisMonth(date: Date): DateRange {
  const result: DateRange = {
    start: startOfMonth(date),
    end: endOfDay(subDays(date, 2)),
  };

  return result;
}

function getDateRangeOfLastMonth(date: Date, maxDate?: Date): DateRange {
  const lastMonth = subMonths(date, 1);
  const firstDayOfLastMonth = startOfMonth(lastMonth);
  const lastDayOfLastMonth = endOfMonth(lastMonth);
  const result: DateRange = {
    start: firstDayOfLastMonth,
    end: endOfDay(maxDate && maxDate <= lastDayOfLastMonth ? maxDate : lastDayOfLastMonth),
  };

  return result;
}

function getDateRangeOfXDaysFromDate(date: Date, days: number): DateRange {
  const result: DateRange = {
    start: startOfDay(subDays(date, days)),
    end: endOfDay(date),
  };

  return result;
}

export function isIn30DaysRange(start: Date, end: Date): boolean {
  // Attention: the order of parameter is reversed.
  return start <= end && differenceInCalendarDays(end, start) <= MAX_PERIOD_DAYS;
}

export function isOlderOrEqualTo2DaysAgo(date: Date): boolean {
  const twoDaysAgo = get2DaysAgo();
  return differenceInDays(date, twoDaysAgo) <= 0;
}

// TODO: this is moved to @bebit/utils, remove from host
export function isWithin2Years(date: Date): boolean {
  const date2YearsAgo = getDate2YearsAgo(new Date());
  return differenceInDays(date2YearsAgo, date) <= 0;
}

// TODO: this is moved to @bebit/utils, remove from host
function getDate2YearsAgo(date: Date): Date {
  const twoYearsAgo = new Date(date);
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  return twoYearsAgo;
}

function get2DaysAgo(): Date {
  const today = startOfToday();
  return subDays(today, 2);
}
